import {
  TEDDIES,
  TEDDY_KEYS,
  teddyPhotoPath,
  teddyPlaceholderPath,
  type TeddyKey,
} from './teddies.js'

/**
 * Who somebody can be on their account.
 *
 * Every teddy, and Daddy — who is not a teddy and deliberately not in that
 * collection. The teddy list is also the card faces in Sky's Memory Match and
 * the roster the games are named from, so adding a person to it would put him
 * face-down on a memory board and count him among ten pairs. Avatars are the
 * superset; the collection stays the collection.
 */
export const PEOPLE = ['daddy'] as const

export type PersonKey = (typeof PEOPLE)[number]

export const AVATAR_KEYS = [...TEDDY_KEYS, ...PEOPLE] as const

export type AvatarKey = TeddyKey | PersonKey

export interface AvatarMeta {
  readonly key: AvatarKey
  readonly name: string
  /** Said after the name, e.g. "Hoppity the rabbit". Empty for a person. */
  readonly description: string
}

const PEOPLE_META: Readonly<Record<PersonKey, AvatarMeta>> = {
  daddy: { key: 'daddy', name: 'Daddy', description: '' },
}

export const AVATARS: Readonly<Record<AvatarKey, AvatarMeta>> = {
  ...(Object.fromEntries(
    TEDDY_KEYS.map((key) => [
      key,
      { key, name: TEDDIES[key].name, description: `the ${TEDDIES[key].species}` },
    ]),
  ) as Record<TeddyKey, AvatarMeta>),
  ...PEOPLE_META,
}

export const ALL_AVATARS: readonly AvatarMeta[] = AVATAR_KEYS.map((key) => AVATARS[key])

export function isAvatarKey(value: string): value is AvatarKey {
  return (AVATAR_KEYS as readonly string[]).includes(value)
}

export function isPersonKey(value: string): value is PersonKey {
  return (PEOPLE as readonly string[]).includes(value)
}

/**
 * The picture for an avatar, whoever it is.
 *
 * A person's photograph lives beside the race art he is drawn for, so he
 * cannot end up looking like two different men in two places.
 */
export function avatarPicture(key: AvatarKey): string {
  return isPersonKey(key) ? `/race/${key}.webp` : teddyPhotoPath(key)
}

/**
 * The drawing to fall back to when a photograph is missing.
 *
 * Not everybody has one, and that is fine. The people were drawn before they
 * were photographed, and so were the first ten teddies; the eleven that came
 * later arrived photographed, so there was never a gap for a stand-in to fill
 * and none was drawn. This names a file that is not there for those, and
 * `fallBackToDrawing` copes by hiding the image — which is what it would do
 * for a drawing that failed to load anyway.
 */
export function avatarPlaceholder(key: AvatarKey): string {
  return isPersonKey(key) ? `/race/${key}.svg` : teddyPlaceholderPath(key)
}

/** "Hoppity the rabbit", or just "Daddy". */
export function avatarName(key: AvatarKey): string {
  const meta = AVATARS[key]
  return meta.description ? `${meta.name} ${meta.description}` : meta.name
}
